"use client";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { atom, useAtom } from "jotai";
import { toast } from "sonner";
import { authClient } from "@constructr-sdk/auth/client";
import { trpcReactQueryApi } from "~/trpc/react";
import type {
  UseMyLibraryParams,
  UseMyLibraryReturn,
} from "~/modules/main/hooks/use-my-library/use-my-library.interface";
import {
  MessageRole,
  Prompt,
  SortOrder,
  Tag,
  TagType,
} from "@constructr-sdk/entities";

// Global state atoms
const sortOrderAtom = atom<SortOrder>(SortOrder.UPDATED);

export function useMyLibrary(
  params: UseMyLibraryParams,
): UseMyLibraryReturn {
  // Memoize params to prevent infinite loops
  const initialOrganizationId = useMemo(
    () => params.initialOrganizationId,
    [params.initialOrganizationId],
  );
  const initialSortOrder = useMemo(
    () => params.initialSortOrder ?? SortOrder.UPDATED,
    [params.initialSortOrder],
  );

  // Global state
  const [sortOrder, setSortOrderState] = useAtom(sortOrderAtom);

  // Auth client queries
  const { data: organizationsData } = authClient.useListOrganizations();
  const { data: activeOrgData } = authClient.useActiveOrganization();

  // Ref to track if we've set an active organization (one-time bootstrap)
  const didSetActiveRef = useRef<boolean>(false);

  // Bootstrap: ensure an active organization is set to prevent 401 errors
  useEffect(() => {
    if (didSetActiveRef.current) return;
    const orgs = organizationsData ?? [];
    if (!activeOrgData?.id && orgs.length > 0) {
      didSetActiveRef.current = true;
      void authClient.organization.setActive({ organizationId: orgs[0]!.id });
    }
  }, [organizationsData, activeOrgData?.id]);

  // TRPC utils for cache invalidation
  const utils = trpcReactQueryApi.useUtils();

  // Determine active organization ID
  const activeOrganizationId = useMemo(() => {
    if (initialOrganizationId) {
      return initialOrganizationId;
    }
    return activeOrgData?.id ?? null;
  }, [initialOrganizationId, activeOrgData?.id]);

  // Prompts query - only enabled when we have an active organization
  const {
    data: promptsData,
    isLoading: isLoadingPrompts,
    error: promptsError,
  } = trpcReactQueryApi.main.prompts.all.useQuery(
    {
      sortOrder,
    },
    {
      enabled: !!activeOrganizationId,
    },
  );

  // Tags queries por tipo
  const {
    data: categoryTags,
    isLoading: isLoadingCategoryTags,
    error: categoryTagsError,
  } = trpcReactQueryApi.main.tags.byType.useQuery(
    { type: TagType.CATEGORY },
    { enabled: !!activeOrganizationId },
  );

  const {
    data: useCaseTags,
    isLoading: isLoadingUseCaseTags,
    error: useCaseTagsError,
  } = trpcReactQueryApi.main.tags.byType.useQuery(
    { type: TagType.USE_CASE },
    { enabled: !!activeOrganizationId },
  );

  const {
    data: roleTags,
    isLoading: isLoadingRoleTags,
    error: roleTagsError,
  } = trpcReactQueryApi.main.tags.byType.useQuery(
    { type: TagType.ROLE },
    { enabled: !!activeOrganizationId },
  );

  const {
    data: industryTags,
    isLoading: isLoadingIndustryTags,
    error: industryTagsError,
  } = trpcReactQueryApi.main.tags.byType.useQuery(
    { type: TagType.INDUSTRY },
    { enabled: !!activeOrganizationId },
  );

  // Create prompt mutation
  const createPromptMutation =
    trpcReactQueryApi.main.prompts.create.useMutation({
      onSuccess: async () => {
        toast.success("Prompt created successfully");
        await utils.main.prompts.all.invalidate();
      },
      onError: (error) => {
        toast.error(`Failed to create prompt: ${error.message}`);
      },
    });

  // Publish prompt mutation
  const publishPromptMutation =
    trpcReactQueryApi.main.prompts.publish.useMutation({
      onSuccess: async () => {
        toast.success("Prompt published successfully");
        await utils.main.prompts.all.invalidate();
      },
      onError: (error) => {
        toast.error(`Failed to publish prompt: ${error.message}`);
      },
    });

  // Initialize sort order on mount
  useEffect(() => {
    setSortOrderState(initialSortOrder);
  }, [initialSortOrder, setSortOrderState]);

  // Derived state
  const prompts = useMemo(() => promptsData ?? [], [promptsData]);
  const promptCount = useMemo(() => prompts.length, [prompts.length]);

  const organizations = useMemo(
    () =>
      (organizationsData ?? []).map((org: {
        id: string; name: string; slug: string; createdAt: Date; logo?: string | null; metadata?: any;
      }) => ({
        id: org.id,
        name: org.name,
        slug: org.slug,
        imageUrl: org.logo ?? null,
        createdAt: org.createdAt,
        updatedAt: null,
      })),
    [organizationsData],
  );

  const tagsByType = useMemo(
    () => ({
      [TagType.CATEGORY]: categoryTags ?? [],
      [TagType.USE_CASE]: useCaseTags ?? [],
      [TagType.ROLE]: roleTags ?? [],
      [TagType.INDUSTRY]: industryTags ?? [],
    }),
    [categoryTags, useCaseTags, roleTags, industryTags],
  );

  const isLoading = useMemo(
    () =>
      isLoadingPrompts ||
      isLoadingCategoryTags ||
      isLoadingUseCaseTags ||
      isLoadingRoleTags ||
      isLoadingIndustryTags,
    [
      isLoadingPrompts,
      isLoadingCategoryTags,
      isLoadingUseCaseTags,
      isLoadingRoleTags,
      isLoadingIndustryTags,
    ],
  );

  const loadError = useMemo(() => {
    if (promptsError) return promptsError.message;
    if (categoryTagsError) return categoryTagsError.message;
    if (useCaseTagsError) return useCaseTagsError.message;
    if (roleTagsError) return roleTagsError.message;
    if (industryTagsError) return industryTagsError.message;
    return null;
  }, [promptsError, categoryTagsError, useCaseTagsError, roleTagsError, industryTagsError]);

  // Mutations
  const setSortOrder = useCallback(
    (order: SortOrder) => {
      setSortOrderState(order);
    },
    [setSortOrderState],
  );

  // Commands
  const initialize = useCallback(async () => {
    // Organizations and active org are automatically loaded by auth client hooks
    // Prompts and tags are automatically loaded by TRPC queries when enabled
    // This function is idempotent and safe to call multiple times
  }, []);

  const selectOrganization = useCallback(
    async (organizationId: string) => {
      try {
        // Set active organization using auth client
        await authClient.organization.setActive({
          organizationId,
        });
        // Invalidate prompts cache to refetch for new organization
        await utils.main.prompts.all.invalidate();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        toast.error(`Failed to switch organization: ${errorMessage}`);
      }
    },
    [utils],
  );

  const createPrompt = useCallback(
    async (
      title: string,
      messages: { role: MessageRole; content: string }[],
    ) => {
      await createPromptMutation.mutateAsync({
        title,
        messages,
      });
    },
    [createPromptMutation],
  );

  const publishPrompt = useCallback(
    async (
      promptId: string,
      categoryTagId: string,
      useCaseTagIds: string[],
      roleTagId: string,
      industryTagIds: string[],
    ) => {
      await publishPromptMutation.mutateAsync({
        promptId,
        categoryTagId,
        useCaseTagIds,
        roleTagId,
        industryTagIds,
      });
    },
    [publishPromptMutation],
  );

  const prefetchPromptDetails = useCallback(
    async (promptId: string) => {
      // Prefetch prompt details for navigation optimization
      await utils.main.prompts.byId.prefetch({ id: promptId });
    },
    [utils],
  );

  return {
    // State
    prompts,
    organizations,
    activeOrganizationId,
    promptCount,
    sortOrder,
    isLoading,
    loadError,
    tagsByType,

    // Mutations
    setSortOrder,

    // Commands
    initialize,
    selectOrganization,
    createPrompt,
    publishPrompt,
    prefetchPromptDetails,
  };
}